import { cn } from "@/lib/utils";

export type StatusNode = {
  /** ステータスコード（例: PENDING_DEPOSIT） */
  code: string;
  /** 日本語ラベル（例: 入金待ち） */
  label: string;
  /** 異常系・終端ステートの場合 true（破線表示） */
  terminal?: boolean;
};

type StatusPipelineProps = {
  nodes: StatusNode[];
  className?: string;
};

/**
 * モノトーン・ミニマリズムのステータス遷移パイプライン。
 * 水平のレールライン上にノードを配置し、コード名とラベルを表示する。
 */
export function StatusPipeline({ nodes, className }: StatusPipelineProps) {
  return (
    <div
      className={cn("overflow-x-auto", className)}
      role="list"
      aria-label="Status pipeline"
    >
      <div className="inline-flex items-start gap-0 py-2">
        {nodes.map((node, i) => {
          const isLast = i === nodes.length - 1;

          return (
            <div
              key={node.code}
              className="flex items-start"
              role="listitem"
            >
              {/* ── Node ── */}
              <div className="flex flex-col items-center">
                {/* Dot */}
                <div
                  className={cn(
                    "relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-background",
                    node.terminal
                      ? "border-dashed border-foreground/25"
                      : "border-foreground/40",
                  )}
                >
                  <span
                    className={cn(
                      "font-mono text-[10px] font-semibold leading-none",
                      node.terminal
                        ? "text-foreground/40"
                        : "text-foreground/80",
                    )}
                  >
                    {i + 1}
                  </span>
                </div>

                {/* Code */}
                <span
                  className={cn(
                    "mt-3 whitespace-nowrap font-mono text-[11px] font-medium leading-none tracking-wide",
                    node.terminal
                      ? "text-foreground/35"
                      : "text-foreground/70",
                  )}
                >
                  {node.code}
                </span>

                {/* Label */}
                <span
                  className={cn(
                    "mt-1.5 whitespace-nowrap text-[11px] leading-none",
                    node.terminal
                      ? "text-muted-foreground/50"
                      : "text-muted-foreground",
                  )}
                >
                  {node.label}
                </span>
              </div>

              {/* ── Connector ── */}
              {!isLast && (
                <div className="flex items-start pt-[15px]">
                  <div className="h-px w-6 bg-foreground/15 sm:w-10" />
                  <svg
                    className="h-[5px] w-[5px] shrink-0 -translate-x-px text-foreground/25"
                    viewBox="0 0 6 6"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M1 0.5L5 3L1 5.5"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="h-px w-6 bg-foreground/15 sm:w-10" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
